
import React, { useEffect, useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [sessionChecked, setSessionChecked] = useState(false);

  // Verificar se estamos em ambiente de desenvolvimento
  const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname.includes('lovable.app');

  useEffect(() => {
    let mounted = true;
    let sessionCheckTimer: NodeJS.Timeout;

    const checkAuth = async () => {
      try {
        console.log("AuthGuard: Checking session...");
        setDebugInfo("Verificando sessão...");
        
        const { data, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("AuthGuard: Session error:", sessionError);
          setDebugInfo(`Erro na sessão: ${sessionError.message}`);
          throw sessionError;
        }

        const session = data?.session;

        if (!session && mounted) {
          console.log("AuthGuard: No session found, redirecting to login");
          setDebugInfo("Nenhuma sessão encontrada, redirecionando para login");
          localStorage.removeItem('currentCompanyId');
          navigate('/login', { replace: true });
          return;
        }

        if (session && mounted) {
          console.log("AuthGuard: Session found, checking user data");
          setDebugInfo("Sessão encontrada, verificando dados do usuário");
          
          // Log dos detalhes da sessão (apenas em desenvolvimento)
          if (isDevelopment) {
            console.log("AuthGuard: Session details:", {
              user: session.user.email,
              expires_at: session.expires_at,
              token: session.access_token ? `${session.access_token.substr(0, 10)}...` : 'none',
            });
          }
          
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('email', session.user.email)
            .maybeSingle();

          if (userError) {
            console.error("AuthGuard: User data error:", userError);
            setDebugInfo(`Erro ao buscar dados do usuário: ${userError.message}`);
            await supabase.auth.signOut();
            localStorage.removeItem('currentCompanyId');
            throw userError;
          }

          if (!userData && mounted) {
            console.log("AuthGuard: No user data found, signing out");
            setDebugInfo("Usuário não encontrado no banco de dados");
            await supabase.auth.signOut();
            localStorage.removeItem('currentCompanyId');
            toast({
              variant: "destructive",
              title: "Erro de Autenticação",
              description: "Usuário não encontrado no sistema.",
            });
            navigate('/login', { replace: true });
            return;
          }

          // Store company ID in localStorage using user-specific key for isolation
          if (userData.company_id) {
            const userSpecificKey = `companyId_${session.user.id}`;
            localStorage.setItem(userSpecificKey, userData.company_id);
            localStorage.setItem('currentCompanyId', userData.company_id);
            console.log('AuthGuard: Company ID saved to localStorage:', userData.company_id);
            console.log('AuthGuard: User-specific company ID key:', userSpecificKey);
            setDebugInfo(`ID da empresa salvo: ${userData.company_id} para usuário: ${session.user.id}`);
          } else {
            console.warn('AuthGuard: User has no company_id:', userData);
            setDebugInfo("Usuário não tem ID de empresa");
          }

          console.log("AuthGuard: Authentication successful");
          setDebugInfo("Autenticação bem-sucedida");
          setSessionChecked(true);
        }
      } catch (error: any) {
        console.error('AuthGuard error:', error);
        setDebugInfo(`Erro de autenticação: ${error.message}`);
        if (mounted) {
          setError(error.message);
          localStorage.removeItem('currentCompanyId');
          navigate('/login', { replace: true });
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
          setIsInitialized(true);
        }
      }
    };

    // Initial auth check
    if (!isInitialized) {
      checkAuth();
    }

    // Verificar sessão periodicamente para evitar perda de sessão
    if (isInitialized && !sessionCheckTimer) {
      sessionCheckTimer = setInterval(async () => {
        try {
          console.log("AuthGuard: Periodic session check");
          const { data } = await supabase.auth.getSession();
          if (!data.session && mounted) {
            console.log("AuthGuard: Session lost during periodic check");
            setDebugInfo("Sessão perdida durante verificação periódica");
            navigate('/login', { replace: true });
          }
        } catch (err) {
          console.error("AuthGuard: Error in periodic session check:", err);
        }
      }, 30000); // Verificar a cada 30 segundos
    }

    // Setup auth state change listener only after initialization
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("AuthGuard: Auth state changed:", event);
      setDebugInfo(`Estado de autenticação alterado: ${event}`);
      
      if (!mounted || !isInitialized) return;

      if (event === 'SIGNED_IN') {
        await checkAuth();
      } else if (event === 'SIGNED_OUT' && mounted) {
        localStorage.removeItem('currentCompanyId');
        // Also remove any user-specific company ID keys
        if (session?.user?.id) {
          localStorage.removeItem(`companyId_${session.user.id}`);
        }
        navigate('/login', { replace: true });
      }
    });

    return () => {
      mounted = false;
      if (sessionCheckTimer) clearInterval(sessionCheckTimer);
      subscription?.unsubscribe();
    };
  }, [navigate, toast, isInitialized, isDevelopment]);

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center gap-4 bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Verificando autenticação...</p>
        {isDevelopment && debugInfo && (
          <p className="text-xs text-muted-foreground">{debugInfo}</p>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center p-4 bg-background">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro de Autenticação</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
          {isDevelopment && debugInfo && (
            <p className="mt-2 text-xs">{debugInfo}</p>
          )}
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthGuard;
